import { InjectionKey, reactive } from 'vue'
import { LocationQuery } from 'vue-router'
import { AxiosInstance } from 'axios'
import { PrefApi } from '@/api/common/pref'
import { QuestionsApi } from '@/api/common/questions'
import { useCommonApiError } from '@/hooks/error/common'
import { IQuestionsStore } from '@/types/common/questions'
import { ApiError } from '@/types/global'

export class QuestionsStore {
  questionsApi: QuestionsApi
  prefApi: PrefApi

  constructor(axios: AxiosInstance) {
    this.questionsApi = new QuestionsApi(axios)
    this.prefApi = new PrefApi(axios)
  }

  public state: IQuestionsStore = reactive({
    categories: [],
    groupedPrefs: {},
    inputs: {
      q1: 0,
      q2: 0,
      q3: 0,
      category: '',
      pref: '',
    },
    result: {
      isRecommend: false,
      selectedCategory: '',
      selectedPref: '',
      photographers: [],
    },
  })

  /**
   * inputs.q1をセット
   * @param value
   */
  public setInputQ1(value: number): void {
    this.state.inputs.q1 = value
  }
  /**
   * inputs.q2をセット
   * @param value
   */
  public setInputQ2(value: number): void {
    this.state.inputs.q2 = value
  }
  /**
   * inputs.q3をセット
   * @param value
   */
  public setInputQ3(value: number): void {
    this.state.inputs.q3 = value
  }
  /**
   * inputs.categoryをセット
   * @param value
   */
  public setInputCategory(value: string): void {
    this.state.inputs.category = value
  }
  /**
   * inputs.prefをセット
   * @param value
   */
  public setInputPref(value: string): void {
    this.state.inputs.pref = value
  }
  /**
   * クエリパラメータをinputsにセット
   * @param query
   */
  public setInputsByQuery(query: LocationQuery): void {
    for (const [key, value] of Object.entries(query)) {
      if (!value || Array.isArray(value)) continue
      switch (key) {
        case 'q1':
        case 'q2':
        case 'q3':
          if (Number.isNaN(Number(value))) continue
          this.state.inputs[key] = Number(value)
          break
        case 'category':
        case 'pref':
          this.state.inputs[key] = value
          break
        default:
          break
      }
    }
  }
  /**
   * inputsをクリア
   */
  public clearInputs(): void {
    this.state.inputs = {
      q1: 0,
      q2: 0,
      q3: 0,
      category: '',
      pref: '',
    }
  }
  /**
   * inputsが空かどうか
   */
  public isEmptyInputs(): boolean {
    return !Object.values(this.state.inputs).some((value) => !!value)
  }
  /**
   * [API] 撮影ジャンル情報取得
   */
  public async getCategories(): Promise<void> {
    await this.questionsApi
      .getCategories()
      .then((response) => {
        this.state.categories = response.categories
      })
      .catch(({ response }: ApiError<null>) => {
        useCommonApiError(response)
      })
  }
  /**
   * [API] 都道府県の情報を取得
   */
  public async getPref(): Promise<void> {
    await this.prefApi
      .getPref()
      .then((response) => {
        this.state.groupedPrefs = response.pref.reduce((a, b) => {
          const key = b.region
          const value = { id: b.id, pref: b.pref }
          return { ...a, [key]: [...(a[key] ?? []), value] }
        }, {} as IQuestionsStore['groupedPrefs'])
      })
      .catch(({ response }: ApiError<null>) => {
        useCommonApiError(response)
      })
  }
  /**
   * [API] 診断結果取得
   */
  public async getResult(): Promise<void> {
    await this.questionsApi
      .getResult(this.state.inputs)
      .then((response) => {
        this.state.result = response
      })
      .catch(({ response }: ApiError<null>) => {
        useCommonApiError(response)
      })
  }
}
export const QuestionsStoreKey: InjectionKey<QuestionsStore> = Symbol('QuestionsStoreKey')
